/**
 * Derivaciones para los KPIs del NOC.
 *
 * IMPORTANTE (honestidad de datos): el backend aún NO expone ingresos ni tarifas
 * por estación (US 2.4). Los ingresos se **estiman** multiplicando la energía de hoy
 * (`overview.energy.todayKwh`, un piso desde `Transactions.totalKwh`) por una tarifa
 * provisional configurable. La UI lo rotula como provisional.
 */
import type { NocActiveSession, NocOverview } from '@goelectric/shared-types';

function readTariff(): number {
  const raw = import.meta.env.VITE_ESTIMATED_TARIFF_COP_PER_KWH;
  const parsed = raw === undefined || raw === '' ? Number.NaN : Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 850;
}

/** Tarifa provisional COP/kWh usada solo para estimar ingresos en la demo. */
export const ESTIMATED_TARIFF_COP_PER_KWH = readTariff();

export interface NocRevenueKpis {
  tariffCopPerKwh: number;
  /** Estimación de ingresos de hoy (energía × tarifa provisional). */
  revenueTodayCop: number;
  /** Estimación de ingresos de las sesiones activas aún no cerradas. */
  revenueInProgressCop: number;
  energyTodayKwh: number;
  /** Histórico acumulado: pendiente (requiere US 2.9 / endpoint de liquidación). */
  revenueAccumulatedCop: number | null;
}

export function deriveRevenue(
  overview: NocOverview | null,
  sessions: NocActiveSession[],
): NocRevenueKpis {
  const energyTodayKwh = overview?.energy.todayKwh ?? 0;
  const energyInProgressKwh = sessions.reduce(
    (total, session) => total + Math.max(0, session.energyKwh ?? 0),
    0,
  );
  return {
    tariffCopPerKwh: ESTIMATED_TARIFF_COP_PER_KWH,
    revenueTodayCop: energyTodayKwh * ESTIMATED_TARIFF_COP_PER_KWH,
    revenueInProgressCop: energyInProgressKwh * ESTIMATED_TARIFF_COP_PER_KWH,
    energyTodayKwh,
    revenueAccumulatedCop: null,
  };
}

/** Disponibilidad de red (%): estaciones online / total. */
export function networkAvailabilityPct(overview: NocOverview | null): number {
  if (!overview || overview.stations.total === 0) {
    return 0;
  }
  return (overview.stations.online / overview.stations.total) * 100;
}

/** Conectores disponibles ahora mismo. */
export function availableConnectors(overview: NocOverview | null): number {
  return overview?.connectors.byState.AVAILABLE ?? 0;
}
